'use client';

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const grainStyle = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='200' height='200' filter='url(%23n)'/%3E%3C/svg%3E")`
};

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleParallax = () => {
      if (!heroRef.current) return;
      const scrollY = window.scrollY;
      const bg = heroRef.current.querySelector('.hero-bg') as HTMLElement;
      if (bg) bg.style.transform = `translateY(${scrollY * 0.4}px)`;
    };
    window.addEventListener('scroll', handleParallax, { passive: true });
    return () => window.removeEventListener('scroll', handleParallax);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center overflow-hidden">

      {/* Beach photo as full background */}
      <div className="hero-bg absolute inset-0 will-change-transform">
        <AppImage
          src="/assets/images/WhatsApp_Image_2026-01-04_at_23.43.47-1771687185998.jpeg"
          alt="Person meditating on the beach at sunset"
          fill
          className="object-cover"
          priority />
        
        {/* Overlay for text readability */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/30 to-black/50" />
      </div>

      {/* Grain texture */}
      <div className="absolute inset-0 opacity-[0.03] bg-repeat z-[1]" style={grainStyle} />

      {/* Main content - positioned to the right */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-28 pb-20 w-full">
        <div className="flex justify-end">
          {/* Text content on the right side */}
          <div className="flex flex-col gap-7 max-w-xl">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 bg-white/95 backdrop-blur-sm rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest w-fit shadow-lg">
              <span className="w-1.5 h-1.5 rounded-full bg-sage inline-block" />
              Staatlich gefördertes Gründungs-Coaching
            </div>

            {/* Headline */}
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-light leading-[1.05] text-white tracking-tight drop-shadow-2xl">
              Deine Geschäftsidee ist kein{' '}
              <span className="italic text-sage-light">Zufall</span>
              {' '}–
              <br />
              <span className="font-normal">sie ist dein Weg.</span>
            </h1>

            {/* Sub-headline */}
            <p className="text-lg md:text-xl text-white/95 leading-relaxed font-light drop-shadow-lg">
              Der Schritt in die Selbstständigkeit – begleitet, strukturiert und{' '}
              <span className="font-semibold text-sage-light">100% kostenfrei durch AVGS-Förderung</span>.
            </p>

            {/* CTA buttons */}
            <div className="flex flex-col sm:flex-row gap-4 mt-2">
              <a
                href="https://calendly.com/azav_consulting/ikigai-karriere-check"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary text-base font-semibold px-8 py-4 rounded-full text-center shadow-xl">
                
                Kostenloses Erstgespräch buchen
              </a>
              <a
                href="#methode"
                className="bg-white/95 backdrop-blur-sm text-charcoal hover:bg-white transition-all text-base font-semibold px-8 py-4 rounded-full text-center shadow-xl">
                
                So funktioniert's
              </a>
            </div>

            {/* Zoom meeting note */}
            <p className="text-sm text-white/80 italic drop-shadow-md -mt-2">
              Das Gespräch findet via Zoom statt
            </p>

            {/* Trust signals */}
            <div className="flex items-center gap-6 pt-2 bg-white/90 backdrop-blur-sm rounded-2xl px-6 py-4 shadow-xl">
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-charcoal">10+</span>
                <span className="text-xs text-muted">Jahre Erfahrung</span>
              </div>
              <div className="w-px h-10 bg-sand/50" />
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-charcoal">100%</span>
                <span className="text-xs text-muted">Kostenfrei (AVGS)</span>
              </div>
              <div className="w-px h-10 bg-sand/50" />
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-charcoal">🚀</span>
                <span className="text-xs text-muted">Von der Idee zur Gründung</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-80 z-10">
        <span className="text-xs text-white uppercase tracking-widest font-medium drop-shadow-lg">Entdecken</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/70 to-transparent animate-pulse" />
      </div>
    </section>);

}